import { collection, doc, runTransaction, serverTimestamp, Timestamp, type DocumentData, type Firestore, type Transaction } from 'firebase/firestore'
import { getTierLimits, hasTierFeature } from '../config/PricingConfig'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import { deriveAuditCategory, isCriticalAuditActionType, shouldWriteAuditEvent } from './auditCategories'

export type AuditRetentionMode = 'STANDARD' | 'FULL'
export type CanonicalAuditActionType = 'CREATE' | 'UPDATE' | 'DELETE' | 'SUBMIT' | 'SCHEDULE' | 'UPLOAD' | 'REVOKE_AUTHORIZATION' | 'EXPORT' | 'SYNC' | 'OTHER'

const STANDARD_RETENTION_MS = 48 * 60 * 60 * 1000
const strippedKey = (key: string) => /email|phone|storagepath|storage_path|token|authorization|header|cookie|secret|password/i.test(key)
const ipKey = (key: string) => /(^|_)(ip|ipaddress|clientip|remoteip)($|_)/i.test(key.replaceAll('-', '_'))

export const maskAuditPayload = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(maskAuditPayload)
  if (value instanceof Date) return value.toISOString()
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).flatMap(([key, entry]) => {
    if (strippedKey(key) || ipKey(key)) return []
    return [[key, maskAuditPayload(entry)]]
  }))
}

export const snapshotAuditTrail = (before: Record<string, unknown> | null | undefined, after: Record<string, unknown> | null | undefined, fields?: string[]) => {
  const keys = fields ?? Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]))
  const changedFields = keys.filter((key) => JSON.stringify(before?.[key] ?? null) !== JSON.stringify(after?.[key] ?? null))
  return {
    changedFields,
    before: maskAuditPayload(Object.fromEntries(changedFields.map((key) => [key, before?.[key] ?? null]))),
    after: maskAuditPayload(Object.fromEntries(changedFields.map((key) => [key, after?.[key] ?? null]))),
  }
}

export const buildAuditSearchPrefixes = (...values: Array<string | null | undefined>) => {
  const tokens = values.join(' ').toLocaleLowerCase().normalize('NFKD').replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter(Boolean)
  return Array.from(new Set(tokens.flatMap((token) => Array.from({ length: Math.min(token.length, 32) }, (_, index) => token.slice(0, index + 1))))).slice(0, 300)
}

export const auditRetentionMode = (workspace: DocumentData | undefined): AuditRetentionMode =>
  workspace?.auditSettings?.fullAuditEnabled === true && (limitsLiftedForWorkspace(workspace) || hasTierFeature(workspace?.plan, 'FULL_AUDIT')) ? 'FULL' : 'STANDARD'

export const auditDeletionTimestamp = (workspace: DocumentData | undefined, now = Date.now()) => {
  if (auditRetentionMode(workspace) === 'STANDARD') return Timestamp.fromMillis(now + STANDARD_RETENTION_MS)
  const days = limitsLiftedForWorkspace(workspace) ? -1 : getTierLimits(workspace?.plan).auditLogRetentionDays
  return days === -1 ? null : Timestamp.fromMillis(now + days * 24 * 60 * 60 * 1000 - 60_000)
}

export const canonicalActionType = (action: string): CanonicalAuditActionType => {
  const value = action.toUpperCase()
  if (value.includes('REVOKE') || value.includes('DISCONNECT')) return 'REVOKE_AUTHORIZATION'
  if (value.includes('SUBMIT')) return 'SUBMIT'
  if (value.includes('SCHEDULE') || value.includes('MEETING')) return 'SCHEDULE'
  if (value.includes('UPLOAD')) return 'UPLOAD'
  if (value.includes('EXPORT')) return 'EXPORT'
  if (value.includes('SYNC')) return 'SYNC'
  if (value.includes('DELETE') || value.includes('REMOVE')) return 'DELETE'
  if (value.includes('CREATE') || value.includes('ADD')) return 'CREATE'
  if (
    value.includes('UPDATE') ||
    value.includes('EDIT') ||
    value.includes('SAVE') ||
    value.includes('CHANGE') ||
    value.includes('ARCHIVE') ||
    value.includes('COMPLETE') ||
    value.includes('REOPEN') ||
    value.includes('ASSIGN') ||
    value.includes('PROOF') ||
    value.includes('DEPENDENCY') ||
    value.includes('STATUS')
  ) return 'UPDATE'
  return 'OTHER'
}

export type AuditEventInput = {
  workspaceId: string
  actorId: string
  actorName?: string | null
  action: string
  actionType?: string
  activityType: string
  targetId: string
  nodeType: string
  targetTitle?: string | null
  summary: string
  payload?: Record<string, unknown>
  snapshot?: ReturnType<typeof snapshotAuditTrail>
  board?: string | null
}

export type NotificationInteractionKind = 'DELIVERED' | 'READ' | 'CLEARED' | 'CLICKED' | 'SETTINGS_CHANGED'

/**
 * One-shot helper for notification governance events. Logs to
 * workspace_activity when a workspaceId is present; otherwise no-ops (personal
 * notifications have no workspace audit surface). The notification body is
 * never persisted into the ledger — only the category + deeplink + ref.
 */
export const logNotificationInteraction = async (
  db: Firestore,
  input: {
    workspaceId: string | null | undefined
    actorId: string
    actorName?: string | null
    kind: NotificationInteractionKind
    notificationId: string
    category?: string | null
    deeplink?: string | null
    sourceRef?: string | null
    eventType?: string | null
  },
) => {
  if (!input.workspaceId) return
  const workspaceRef = doc(db, 'workspaces', input.workspaceId)
  await runTransaction(db, async (transaction) => {
    const workspaceSnap = await transaction.get(workspaceRef)
    const workspace = workspaceSnap.exists() ? workspaceSnap.data() : undefined
    logAuditEvent(db, transaction, workspace, {
      workspaceId: input.workspaceId as string,
      actorId: input.actorId,
      actorName: input.actorName ?? null,
      action: `NOTIFICATION_${input.kind}`,
      activityType: 'NOTIFICATION_INTERACTION',
      targetId: input.notificationId,
      nodeType: 'NOTIFICATION',
      targetTitle: input.category ?? 'Notification',
      summary: `Notification ${input.kind.toLowerCase()}`,
      payload: {
        category: input.category ?? null,
        deeplink: input.deeplink ?? null,
        sourceRef: input.sourceRef ?? null,
        eventType: input.eventType ?? null,
      },
    })
  })
}

export const logAuditEvent = (db: Firestore, transaction: Transaction, workspace: DocumentData | undefined, input: AuditEventInput) => {
  if (!shouldWriteAuditEvent(workspace, input)) return null
  const ref = doc(collection(db, 'workspace_activity'))
  const maskedPayload = maskAuditPayload(input.payload ?? {}) as Record<string, unknown>
  const actorName = input.actorName && !input.actorName.includes('@') ? input.actorName : 'Workspace member'
  const actionType = input.actionType ?? canonicalActionType(input.action)
  const deletionTimestamp = isCriticalAuditActionType(actionType) ? null : auditDeletionTimestamp(workspace)
  const auditCategory = deriveAuditCategory(input)
  transaction.set(ref, {
    workspaceId: input.workspaceId,
    actorId: input.actorId,
    actorName,
    action: input.action,
    actionType,
    activityType: input.activityType,
    targetId: input.targetId,
    nodeType: input.nodeType,
    targetType: input.nodeType,
    targetTitle: input.targetTitle ?? 'Workspace item',
    summary: input.summary,
    maskedPayload,
    metadata: maskedPayload,
    snapshot: input.snapshot ? maskAuditPayload(input.snapshot) : null,
    auditCategory,
    auditSearchPrefixes: buildAuditSearchPrefixes(input.targetTitle, input.summary, input.action, actorName),
    retentionMode: auditRetentionMode(workspace),
    board: input.board ?? null,
    createdAt: serverTimestamp(),
    ...(deletionTimestamp ? { deletionTimestamp } : {}),
  })
  return ref.id
}
